import { RadioGroupField } from 'lib/iterable-hook-form';
import { useCustomEventsQueryContext } from 'modules/customEvents/contexts';
import { Project } from 'modules/customEvents/types';
import { customEventsOptions, userFieldsOptions } from 'modules/dataDictionary/constants';
import { SettingsFormProvider } from 'modules/dataDictionary/contexts';
import { useSettingsContent } from 'modules/dataDictionary/hooks';
import { createDefaultSettings } from 'modules/dataDictionary/utils';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { PrimaryButton } from 'ui/button';
import { WarningFill16 } from 'ui/icons';
import { Body2, H3 } from 'ui/theme';
import {
  Card,
  SettingsWrapper,
  StyledFixedWidth,
  StyledFooter,
  StyledHeader,
  TextWrapper,
} from './SettingsContent.styled';

export const SETTINGS_CONTENT_TEXT = {
  customEventFieldsSectionId: 'custom-event-fields-section',
  sectionTestId: 'settings-content',
  userFieldsSectionId: 'user-fields-section',
};

type SettingsProps = {
  project: Project;
  refetchProject: () => void;
};

/** Settings UI form fields and custom hook to manage logic */
export const Settings = ({ project = {} as Project, refetchProject }: SettingsProps) => {
  const { formState, onSubmit } = useSettingsContent(project, refetchProject);
  const { isDirty, isSubmitting, isSubmitted, isValid } = formState;

  return (
    <StyledFixedWidth data-test={SETTINGS_CONTENT_TEXT.sectionTestId}>
      <TextWrapper>
        <H3>Settings</H3>
        <Body2>
          Control how Schema Management behaves for this project, such as how custom events and
          fields are handled during list uploads and API calls.
        </Body2>
      </TextWrapper>
      <SettingsWrapper>
        <Card data-test={SETTINGS_CONTENT_TEXT.userFieldsSectionId}>
          <StyledHeader>User profiles</StyledHeader>
          <RadioGroupField isBordered name="userFieldValue" options={userFieldsOptions} />
        </Card>
        <Card data-test={SETTINGS_CONTENT_TEXT.customEventFieldsSectionId}>
          <StyledHeader>Custom events</StyledHeader>
          <RadioGroupField isBordered name="customEventValue" options={customEventsOptions} />
        </Card>
      </SettingsWrapper>
      <StyledFooter>
        {isDirty && (
          <Banner Icon={<WarningFill16 />} isSubtle variant={BannerVariants.NEUTRAL}>
            <span>Unsaved changes</span>
          </Banner>
        )}
        <PrimaryButton
          onClick={onSubmit}
          isLoading={isSubmitting}
          isDisabled={Boolean(isSubmitted && !isValid) || !isDirty}
        >
          Save changes
        </PrimaryButton>
      </StyledFooter>
    </StyledFixedWidth>
  );
};

/** Settings component wrapper to add form provider */
export const SettingsContent = () => {
  const {
    isLoadingProjectSettings,
    project = {} as Project,
    refetchProject,
  } = useCustomEventsQueryContext();
  const { customEventValue, userFieldValue } = createDefaultSettings(project);

  return (
    <>
      {!isLoadingProjectSettings && (
        <SettingsFormProvider customEventValue={customEventValue} userFieldValue={userFieldValue}>
          <Settings project={project} refetchProject={refetchProject} />
        </SettingsFormProvider>
      )}
    </>
  );
};
